import { Brand } from '../../../elements/entities/branding.entity';
import { hasOwnPropertyGuard } from '../../common/helpers/objects/has-own-property-guard.helper';
import { OrganisationId } from '../../organisations/entities/organisation.entity';
import { Family, Identifier } from '../../records/entities/metadata/metadata.entity';

export interface FieldDefinitionChildReference {
    FlatKey: string;
    FieldDefinitionId: number;
    DottedKey: string;
}

export type DottedKey = Brand<string, 'DottedKey'>;

export function getParentDottedKey(dottedKey: DottedKey): DottedKey {
    const index: number = dottedKey.lastIndexOf('.');

    return dottedKey.slice(0, index) as DottedKey;
}

export type BaseFieldDefinition = BaseFlatFieldDefinition | BaseNestedFieldDefinition;

export enum FieldDefinitionStatus {
    Draft = 'Draft',
    Processing = 'Processing',
    Published = 'Published'
}

export enum FieldDefinitionInitiator {
    System = 'System',
    Migration = 'Migration',
    User = 'User'
}

/**
 * FieldDefinition model exposed by API
 */
export interface ApiFieldDefinition {
    FieldDefinitionId: number;
    ParentId: number | null;
    ParentDottedKey: DottedKey | null;
    Key: string;
    FlatKey: string;
    DottedKey: DottedKey;
    Family: Family;
    Type: FieldType;
    Status: FieldDefinitionStatus;
    Version: number;
    Initiator?: FieldDefinitionInitiator;
    ReadOnly: boolean;
    Required: boolean;
    WriteOnce: boolean;
    Searchable: boolean;
    GlobalSearch: boolean;
    MultiValued?: boolean;
    Sortable: boolean;
    IndexName: string | null;
    Inheritance: Inheritance;
    Scheme?: ThesaurusScheme;
    Translations: FieldDefinitionTranslation;
    LongTranslations: FieldDefinitionTranslation;

    // Unique for EnumField
    PossibleValues?: string[];
    Values?: EnumValue[];

    // Unique for ThesaurusLabelField
    Lang?: string;

    // Unique for VectorField
    Dimensions?: number;

    Context?: {
        IsEditable: boolean;
        IsDeletable: boolean;
        HasEditableChildren: boolean;
    };
}

/**
 * Extra fields that are added in the FieldDefinitionService.
 * Used to enhance useability in code.
 */
export interface ShFieldDefinition extends ApiFieldDefinition {
    Parent?: BaseFieldDefinition; // Practical, manual enhancement to easily read out the Parent FieldDefinition
    Label: string; // Practical, manual enhancement to easily read out the Label
    LongLabel: string; // Practical, manual enhancement to easily read out the LongLabel
}

export interface BaseFlatFieldDefinition extends ShFieldDefinition {
    ChildrenFlat?: FieldDefinitionChildReference[];
}

export interface BaseNestedFieldDefinition extends ShFieldDefinition {
    Children?: this[];
}

export enum FieldType {
    SimpleField = 'SimpleField',
    TextField = 'TextField',
    HiddenField = 'HiddenField',
    BooleanField = 'BooleanField',
    EnumField = 'EnumField',
    LongField = 'LongField',
    FramesField = 'FramesField',
    DoubleField = 'DoubleField',
    TimeCodeField = 'TimeCodeField',
    DateField = 'DateField',
    EDTFField = 'EDTFField',
    TimerField = 'TimerField',
    ListField = 'ListField',
    MultiItemField = 'MultiItemField',
    PositionField = 'PositionField',
    GeoCoordinateField = 'GeoCoordinateField',
    UrlField = 'UrlField',
    VectorField = 'VectorField',
    MapField = 'MapField',
    ThesaurusField = 'ThesaurusField',
    ThesaurusUriField = 'ThesaurusUriField',
    ThesaurusLabelField = 'ThesaurusLabelField',
    EnumMapField = 'EnumMapField',
    ComplexField = 'ComplexField',
    ComplexListField = 'ComplexListField',
    ComplexMapField = 'ComplexMapField',
    OrderedMultiItemField = 'OrderedMultiItemField',
    FieldDefinitionReferenceField = 'FieldDefinitionReferenceField'
}

export const SimpleTypes: FieldType[] = [
    FieldType.SimpleField,
    FieldType.BooleanField,
    FieldType.EnumField,
    FieldType.HiddenField,
    FieldType.TimeCodeField,
    FieldType.LongField,
    FieldType.FramesField,
    FieldType.DoubleField,
    FieldType.DateField,
    FieldType.TextField,
    FieldType.GeoCoordinateField,
    FieldType.EDTFField,
    FieldType.ThesaurusUriField,
    FieldType.ThesaurusLabelField,
    FieldType.UrlField,
    FieldType.VectorField,
    FieldType.FieldDefinitionReferenceField
];

export const ComplexTypes: FieldType[] = [
    FieldType.ComplexField,
    FieldType.MultiItemField,
    FieldType.ListField,
    FieldType.MapField,
    FieldType.PositionField,
    FieldType.OrderedMultiItemField,
    FieldType.ComplexMapField,
    FieldType.ComplexListField,
    FieldType.ThesaurusField,
    FieldType.TimerField
];

export interface FieldDefinitionTranslation {
    [key: string]: string;
}

export interface FieldDefinitionValue {
    OrganisationId: number;
    Active: boolean;
    Value: FieldDefinitionValueContent;
}

export interface FieldDefinitionValueContent {
    Id: string;
    Label: string;
    Values: {
        [key: string]: string;
    };
}

export enum Inheritance {
    None = 'None',
    Creation = 'Creation',
    Propagation = 'Propagation'
}

export interface EnumValue {
    Value: {
        Label: string;
    };
    Active: boolean;
}

export const BaseNestedFieldDefinitionGuard: (f: BaseFieldDefinition) => f is BaseNestedFieldDefinition = (
    f: BaseFieldDefinition
): f is BaseNestedFieldDefinition => {
    return hasOwnPropertyGuard(f, 'Children');
};

export const BaseFlatFieldDefinitionGuard: (f: BaseFieldDefinition) => f is BaseFlatFieldDefinition = (
    f: BaseFieldDefinition
): f is BaseFlatFieldDefinition => {
    return hasOwnPropertyGuard(f, 'ChildrenFlat');
};

export interface ThesaurusScheme {
    ExternalThesaurusId: string;
    Id: string;
    MaxNarrower: number;
    MinNarrower: number;
    Name: string;
    TopConcept: string;
}

export interface Thesaurus {
    Id: Identifier;
    Name: string;
    OrganisationId: OrganisationId;
    ExternalId: Identifier;
    LastUpdatedInIndex: string;
    CreationDate: string;
    LastModifiedDate: string;
}

export type FieldDefinition = BaseFlatFieldDefinition | BaseNestedFieldDefinition;
